import { useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useTestIntegrationMutation } from "src/queries/platform/system.mutation";
import { DeleteOutlineIcon, EditIcon } from "@ui-icons";
import {
  INTEGRATION_TYPES,
  type IntegrationEntry,
  type IntegrationTypeDef,
} from "../../SystemSettings.integrations";
import SystemSettingsIntegrationDialog from "./components/SystemSettingsIntegrationDialog/SystemSettingsIntegrationDialog";

interface SystemSettingsIntegrationsProps {
  form: any;
}

type IntegrationRow = {
  def: IntegrationTypeDef;
  index: number;
  // Not every type has a name (a Databricks workspace has only its URL).
  name: string | null;
  detail: string;
  enabled: boolean;
};

type Session = {
  def: IntegrationTypeDef;
  index: number;
  draft: IntegrationEntry;
  open: boolean;
};

export default function SystemSettingsIntegrations({
  form,
}: SystemSettingsIntegrationsProps) {
  return (
    <form.Subscribe
      selector={(state: any) =>
        INTEGRATION_TYPES.map((def) => def.parse(state.values[def.field]))
      }
      children={(lists: IntegrationEntry[][]) => (
        <SectionContent form={form} lists={lists} />
      )}
    />
  );
}

function SectionContent({
  form,
  lists,
}: {
  form: any;
  lists: IntegrationEntry[][];
}) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // null = nothing mounted. `open` stays false through the exit fade.
  const [session, setSession] = useState<Session | null>(null);
  const testIntegration = useTestIntegrationMutation();

  const listOf = (def: IntegrationTypeDef) => lists[INTEGRATION_TYPES.indexOf(def)];

  const rows: IntegrationRow[] = INTEGRATION_TYPES.flatMap((def) =>
    listOf(def).map((entry, index) => ({
      def,
      index,
      enabled: entry.enabled,
      ...def.row(entry),
    }))
  );

  const write = (def: IntegrationTypeDef, next: IntegrationEntry[]) =>
    form.setFieldValue(def.field, next);

  const requestClose = () =>
    setSession((current) => (current?.open ? { ...current, open: false } : current));

  const save = () => {
    if (!session?.open) return;
    const entry = session.def.toPayload(session.draft);
    const next = [...listOf(session.def)];
    if (session.index === -1) next.push(entry);
    else next[session.index] = entry;
    write(session.def, next);
    setSession((current) => (current?.open ? { ...current, open: false } : current));
  };

  const setEnabled = (row: IntegrationRow, enabled: boolean) =>
    write(
      row.def,
      listOf(row.def).map((entry, i) =>
        i === row.index ? { ...entry, enabled } : entry
      )
    );

  const removeRow = (row: IntegrationRow) =>
    write(
      row.def,
      listOf(row.def).filter((_, i) => i !== row.index)
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-1">
        <div className="text-xl font-medium">Integrations</div>
        <Tooltip
          title="Connect external systems. Changes are applied when you press Submit."
          arrow
        >
          <InfoOutlinedIcon
            sx={{ fontSize: "18px", color: "grey.400", cursor: "help" }}
          />
        </Tooltip>
        <div className="flex-1" />
        <Button
          variant="outlined"
          size="small"
          endIcon={<ArrowDropDownIcon />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          data-test-id="add-integration-button"
        >
          Add Integration
        </Button>
        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(null)}
          data-test-id="add-integration-menu"
        >
          {INTEGRATION_TYPES.map((def) => (
            <MenuItem
              key={def.type}
              disabled={
                def.maxEntries !== undefined && listOf(def).length >= def.maxEntries
              }
              onClick={() => {
                setMenuAnchor(null);
                setSession({
                  def,
                  index: -1,
                  draft: def.empty(),
                  open: true,
                });
              }}
              data-test-id={`add-integration-${def.type}`}
            >
              {def.label}
            </MenuItem>
          ))}
        </Menu>
      </div>

      {!rows.length ? (
        <p className="py-4 text-center text-sm text-text-secondary">
          No integrations connected. Click "Add Integration" to connect an external
          system, then press Submit to save.
        </p>
      ) : (
        <Table size="small" data-test-id="integrations-table">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>URL / Account</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={`${row.def.type}-${row.detail}-${row.index}`}>
                <TableCell>
                  <Chip label={row.def.label} size="small" variant="outlined" />
                </TableCell>
                <TableCell className="max-w-48 truncate">
                  {row.name || "—"}
                </TableCell>
                <TableCell className="max-w-72 truncate">{row.detail}</TableCell>
                <TableCell>
                  <Switch
                    size="small"
                    checked={row.enabled}
                    onChange={(_, checked) => setEnabled(row, checked)}
                  />
                </TableCell>
                <TableCell align="right" className="whitespace-nowrap">
                  <IconButton
                    size="small"
                    aria-label={`Edit ${row.def.label} integration`}
                    onClick={() => {
                      setSession({
                        def: row.def,
                        index: row.index,
                        draft: listOf(row.def)[row.index],
                        open: true,
                      });
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Remove ${row.def.label} integration`}
                    onClick={() => removeRow(row)}
                  >
                    <DeleteOutlineIcon fontSize="small" color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {session && (
        <SystemSettingsIntegrationDialog
          type={session.def.type}
          dialogTitle={session.def.dialogTitle}
          index={session.index}
          isOpen={session.open}
          canSave={session.def.canSave(session.draft)}
          onClose={requestClose}
          onSave={save}
          onTest={
            session.def.testable
              ? () =>
                  testIntegration.mutate({
                    type: session.def.type,
                    entry: session.def.toPayload(session.draft),
                  })
              : undefined
          }
          isTesting={testIntegration.isPending}
          onExited={() =>
            setSession((current) => (current && !current.open ? null : current))
          }
        >
          <session.def.Fields
            entry={session.draft}
            onChange={(draft: IntegrationEntry) =>
              setSession((current) => (current ? { ...current, draft } : current))
            }
          />
        </SystemSettingsIntegrationDialog>
      )}
    </div>
  );
}
